import { FormikConfig, FormikHelpers, FormikValues } from "formik/dist/types";
import { diffFields, FieldDiffer } from "./diffFields";
import ReactGA from "react-ga";
import { datadogRum } from "@datadog/browser-rum";
import { Schema } from "yup";
import { datadogLogs } from "@datadog/browser-logs";
import { DimensionValues, CustomDimensions } from "../../../monitoring";
import { NonNullableRequired } from "utilities/types";

export type SubmitTracker = <Values extends FormikValues = FormikValues>(
  initialValues: NonNullableRequired<Values>,
  onSubmit: FormikConfig<Values>["onSubmit"],
  validateSchema?: Schema<any>,
  customDimensions?: Partial<
    Pick<DimensionValues, "model" | "software_version">
  >
) => (v: Values, h: FormikHelpers<Values>) => void | Promise<any>;
export type NeedsNameTracker = (name: string) => SubmitTracker;
/**
 * A higher order method to build a submit action for a formik form.
 * goes FieldDiffer method => props on form => on submit handler;
 * Used like:
 * const diffTrackingSubmit = higherOrderSubmit(differ);
 * const diffTrackerForForm = diffTrackingSubmit("MyWonderfulForm")
 * <Formik
 * @param differ
 */
export const higherOrderSubmit: (
  differ: FieldDiffer
) => NeedsNameTracker = differ => name => (
  initialValues,
  onSubmit,
  validationSchema,
  customDimensions
) => (v, h) => {
  try {
    const model: string | null | undefined = customDimensions?.model;
    const software_version: string | null | undefined =
      customDimensions?.software_version;

    //ga('send', 'event', [eventCategory], [eventAction], [eventLabel], [eventValue], [fieldsObject]);
    ReactGA.ga("send", "event", name, "Submitted", `${name}-Submitted`, {
      [CustomDimensions.model]: model,
      [CustomDimensions.software_version]: software_version,
      nonInteraction: false
    });

    const diffs = differ(initialValues, v, validationSchema);
    datadogRum.addUserAction("Submitted", {
      form: name,
      fields: diffs,
      model: model ?? "not specified",
      software_version: software_version ?? "not specified"
    });
    diffs.forEach(field => {
      ReactGA.ga("send", "event", name, "Changed Field", field, {
        [CustomDimensions.model]: model,
        [CustomDimensions.software_version]: software_version,
        nonInteraction: false
      });
      datadogRum.addUserAction("Changed Field", {
        form: name,
        field: field,
        model: model ?? "not specified",
        software_version: software_version ?? "not specified"
      });
    });
  } catch (e) {
    datadogLogs.logger.error("Error on Differ", {
      exception: e?.message ?? e
    });
  }
  return onSubmit(v, h);
};
export const defaultSubmitTracker = higherOrderSubmit(diffFields);
export const nullTacker = higherOrderSubmit(() => []);
